import { updateExamApi } from "@/api/services/admin/learning-content";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { Form } from "@/components/elements/Form";
import { MenuNavigate } from "@/components/elements/MenuNavigate";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { BasicInfoForm } from "@/features/learning-content/components/ExamForm/BasicInfoForm";
import { ProceduresForm } from "@/features/learning-content/components/ExamForm/ProceduresForm";
import { RelatedLinksForm } from "@/features/learning-content/components/ExamForm/RelatedLinksForm";
import { useExamDetail } from "@/features/learning-content/hooks/useExamDetail";
import { useIndustryDetail } from "@/features/learning-content/hooks/useIndustryDetail";
import { examSchema } from "@/features/learning-content/schema/ExamSchema";
import { TExamCreateForm } from "@/features/learning-content/types/learningContent";
import { convertExamDetailToForm, convertExamFormToUpdateReq } from "@/features/learning-content/utils/converts";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { setErrorForm, showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, Typography } from "@mui/material";
import { styled } from "@mui/system";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TExamEditPageProps = {};

export const ExamEditPage: FC<TExamEditPageProps> = () => {
  const { id, examId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);

  const { data: industryData } = useIndustryDetail(id);
  const { data: examDetail, isLoading: isExamLoading } = useExamDetail(examId);

  const initialValues = useMemo<TExamCreateForm>(() => {
    if (examDetail) {
      return convertExamDetailToForm(examDetail);
    }
    return {
      periodName: "",
      status: "active",
      examMethod: "",
      examMonth: null,
      examOverview: "",
      examInfoSource: "",
      internalMemo: "",
      relatedLinks: [{ label: "", url: "" }],
      procedures: [{ agent: "", site: null, stepName: "", description: null, duration: null, deadline: null }],
      examDayDescription: "",
      passFailDescription: "",
      announcementEstimate: "",
    };
  }, [examDetail]);

  const methods = useForm<TExamCreateForm>({
    defaultValues: initialValues,
    values: initialValues,
    resolver: yupResolver(examSchema) as never,
    mode: "all",
    reValidateMode: "onBlur",
  });

  const onSubmit = async (data: TExamCreateForm) => {
    if (!examId) return;

    setLoading(true);
    try {
      const payload = convertExamFormToUpdateReq(data);
      const rs = await updateExamApi(examId, payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_EXAM_DETAIL, examId] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_DETAIL, id] });
      if (id) {
        navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
    setLoading(false);
  };

  const handleBack = () => {
    if (id) {
      navigate(getDynamicRoute(AppRoutes.industryDetail, { id }));
    } else {
      navigate(AppRoutes.learningContent);
    }
  };

  if (isExamLoading) {
    return null;
  }

  return (
    <PageWrapper gap={4}>
      <TopPage
        title={
          <Typography variant="head32Bold" display="flex" alignItems="center" gap={2}>
            教材と試験情報の管理 - 試験情報の編集
          </Typography>
        }
        description={<Typography variant="body14">試験情報を変更・修正できます。カリキュラムに影響のある項目は変更できません。</Typography>}
        enablePageBack={false}
      />
      <Form methods={methods} onSubmit={onSubmit} schema={examSchema} initialValues={initialValues}>
        <MenuNavigate
          menuPrefix={
            <Box sx={{ p: "16px 16px 12px 16px" }}>
              <Typography variant="body14" sx={{ fontWeight: 700, color: "text.primary", fontSize: "14px" }}>
                {industryData?.name ?? "分野名"}
              </Typography>
            </Box>
          }
          contents={[
            {
              title: "基本情報",
              content: (
                <Stack gap={3}>
                  <BasicInfoForm disableExamMethod={examDetail?.examMethod === "cbt"} />
                  <RelatedLinksForm />
                </Stack>
              ),
            },
            {
              title: "受験手続き情報",
              content: <ProceduresForm />,
            },
          ]}
          contentWidth="900px"
          contentAffix={
            <FooterContainer>
              <Stack gap={1} direction="row" justifyContent="space-between" alignItems="center">
                <Button size="sm-round" variant="secondary" onClick={handleBack}>
                  戻る
                </Button>
                <Button disabled={loading} size="sm-round" type="submit">
                  保存
                </Button>
              </Stack>
            </FooterContainer>
          }
        />
      </Form>
    </PageWrapper>
  );
};

const PageWrapper = styled(Stack)`
  width: 1198px;
  margin: 0 auto;
  margin-top: 8px;
`;
